import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FeatTiersService } from '../feat-tiers.service';
import { ChecklistSelection, DropdownCheckOption } from 'src/app/shared/component/dropdown-checkbox-v2/dropdown-checkbox-v2.component';
import * as vo from 'src/app/core/model/server/vo/_index';

export interface TierFeatureDialogData {
  mode: 'create' | 'edit';
  feature?: vo.TierFeaturesResult;
}

@Component({
  selector: 'app-modify-tier-modal',
  templateUrl: './modify-tier-modal.component.html',
  styleUrl: './modify-tier-modal.component.scss'
})
export class ModifyTierModalComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private originalAllowed: number[] = [];
  private originalDenied:  number[] = [];

  form!: UntypedFormGroup;
  isEditMode = false;
  apiError: string | null = null;

  tierOptions: DropdownCheckOption[] = [];

  allowedSelectedValues: string[] = [];
  deniedSelectedValues:  string[] = [];

  allowedOptions: DropdownCheckOption[] = [];
  deniedOptions:  DropdownCheckOption[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<ModifyTierModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TierFeatureDialogData,
    private featTiersService: FeatTiersService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.isEditMode = this.data?.mode === 'edit';

    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.pattern('^[a-zA-Z ]+$')]],
    });

    // In edit mode, name is read-only — disable it so it doesn't affect form.valid
    if (this.isEditMode) {
      this.form.get('name')?.disable();
    }

    this.featTiersService.getAccountTiers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: vo.AccountTiersResponse) => {
          this.tierOptions = (res.result ?? []).map(t => ({
            label: `Tier ${t.tierId} (${t.tierName})`,
            value: String(t.tierId),
          }));

          const allValues = this.allTierValues();

          if (this.isEditMode && this.data.feature) {
            this.allowedSelectedValues = (this.data.feature.allowedTiers ?? [])
              .map(String)
              .filter(v => allValues.includes(v));

            this.deniedSelectedValues = (this.data.feature.deniedTiers ?? [])
              .map(String)
              .filter(v => allValues.includes(v));

            const unassigned = allValues.filter(
              v => !this.allowedSelectedValues.includes(v) && !this.deniedSelectedValues.includes(v)
            );
            this.deniedSelectedValues = [...this.deniedSelectedValues, ...unassigned];

            this.originalAllowed = this.allowedSelectedValues.map(Number);
            this.originalDenied  = this.deniedSelectedValues.map(Number);
          } else {
            this.allowedSelectedValues = [];
            this.deniedSelectedValues  = [];
          }

          this.rebuildOptions();
        },
        error: () => {
          this.apiError = 'Failed to load tiers. Please try again.';
        },
      });

    if (this.isEditMode && this.data.feature) {
      this.form.patchValue({ name: this.data.feature.featureName });
      this.form.markAllAsTouched();
    }

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => { this.apiError = null; });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  /** All valid tier values from the loaded tierOptions. */
  private allTierValues(): string[] {
    return this.tierOptions
      .filter(o => o.value !== null)
      .map(o => o.value!);
  }

  // ─── Dropdown sync handlers ───────────────────────────────────────────────

  /**
   * CREATE: selecting a tier in allow → auto-selects it in deny too (it starts neutral).
   *         unselecting from allow → auto-selects in deny.
   * EDIT:   unselecting from allow → the complement goes to deny.
   *
   * Rule: allow + deny always cover all tiers. Whatever leaves allow goes to deny.
   */
  onAllowedChange(event: ChecklistSelection): void {
    const allValues = this.allTierValues();

    // Guard: only keep values that exist in tierOptions
    this.allowedSelectedValues = event.selectedValues.filter(v => allValues.includes(v));

    // Everything not in allowed → goes to denied
    this.deniedSelectedValues = allValues.filter(v => !this.allowedSelectedValues.includes(v));

    this.apiError = null;
    this.rebuildOptions();
  }

  /**
   * CREATE: selecting a tier in deny → removes it from allow (was neutral before).
   *         unselecting from deny → auto-selects in allow.
   * EDIT:   unselecting from deny → complement goes to allow.
   *
   * Rule: allow + deny always cover all tiers. Whatever leaves deny goes to allow.
   */
  onDeniedChange(event: ChecklistSelection): void {
    const allValues = this.allTierValues();

    // Guard: only keep values that exist in tierOptions
    this.deniedSelectedValues = event.selectedValues.filter(v => allValues.includes(v));

    // Everything not in denied → goes to allowed
    this.allowedSelectedValues = allValues.filter(v => !this.deniedSelectedValues.includes(v));

    this.apiError = null;
    this.rebuildOptions();
  }

  // ─── Option builder ───────────────────────────────────────────────────────

  private rebuildOptions(): void {
    this.allowedOptions = this.tierOptions
      .filter(o => o.value !== null)
      .map(o => ({ ...o, disabled: false })); // allow: never disabled

    this.deniedOptions = this.tierOptions
      .filter(o => o.value !== null)
      .map(o => ({
        ...o,
        disabled: this.allowedSelectedValues.includes(o.value!), // deny: disabled if already allowed
      }));
  }

  // ─── Whole-dropdown disabled state ────────────────────────────────────────

  get allowedDisabled(): boolean { return false; }

  get deniedDisabled(): boolean {
    return this.tierOptions.length > 0 &&
      this.allTierValues().every(v => this.allowedSelectedValues.includes(v));
  }

  // ─── Create-mode assignment guard ─────────────────────────────────────────

  /**
   * In create mode, the user must assign every tier to either allow or deny
   * before saving. In edit mode this is always satisfied since we pre-fill.
   */
  get allTiersAssigned(): boolean {
    if (this.isEditMode) { return true; }
    const allValues = this.allTierValues();
    return allValues.every(
      v => this.allowedSelectedValues.includes(v) || this.deniedSelectedValues.includes(v)
    );
  }

  // ─── Validation ───────────────────────────────────────────────────────────

  getFieldError(field: string): string | null {
    const ctrl = this.form.get(field);
    if (!ctrl || !ctrl.invalid || !ctrl.touched) { return null; }
    if (field === 'name') {
      if (ctrl.hasError('required'))      { return 'Feature name is required (min. 3 characters).'; }
      if (ctrl.hasError('minlength'))     { return 'Feature name is required (min. 3 characters).'; }
      if (ctrl.hasError('pattern'))   { return 'Some special characters are not allowed.';}
      if (ctrl.hasError('alreadyExists')) { return 'Feature name already exists.'; }
    }
    return null;
  }

  isInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && ctrl.touched);
  }

  get hasChanges(): boolean {
    if (!this.isEditMode) { return true; }

    const currentAllowed = [...this.allowedSelectedValues.map(Number)].sort((a, b) => a - b);
    const currentDenied  = [...this.deniedSelectedValues.map(Number)].sort((a, b) => a - b);

    // originalDenied may not include unassigned tiers that we defaulted in,
    // so compare against the full complement instead
    const allTierIds = this.allTierValues().map(Number).sort((a, b) => a - b);
    const expectedDenied = allTierIds.filter(v => !currentAllowed.includes(v)).sort((a, b) => a - b);
    const originalDeniedFull = allTierIds.filter(v => !this.originalAllowed.includes(v)).sort((a, b) => a - b);

    const sameAllowed = JSON.stringify(currentAllowed.sort((a, b) => a - b)) === 
                        JSON.stringify([...this.originalAllowed].sort((a, b) => a - b));
    const sameDenied  = JSON.stringify(expectedDenied) === 
                        JSON.stringify(originalDeniedFull);
                        
    return !(sameAllowed && sameDenied);
  }

  get isFormValid(): boolean {
    if (this.isEditMode) {
      return this.hasChanges;
    }
    return this.form.valid && this.hasChanges && this.allTiersAssigned; 
  }

  // ─── Submit ───────────────────────────────────────────────────────────────

  store(): void {
    if (!this.isFormValid) {
      this.form.markAllAsTouched();
      return;
    }

    this.spinner.show().catch(console.error);
    const raw = this.form.getRawValue();

    const tiersToAllow = this.allowedSelectedValues.map(Number);
    const tiersToDeny  = this.deniedSelectedValues.map(Number);

    const request$ = this.isEditMode && this.data.feature
      ? this.featTiersService.updateTierFeature({
          featureId: this.data.feature.uid,
          tiersToAllow,
          tiersToDeny,
        })
      : this.featTiersService.createTierFeature({
          name: raw.name,
          tiersToAllow,
          tiersToDeny,
        });

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        if (res.status === 200) {
          this.toastr.success(
            this.isEditMode
              ? `Details for ${this.data.feature?.featureName} has been successfully updated!`
              : 'New feature has been successfully added!',
          );
          this.dialogRef.close(true);
        } else if (!this.isEditMode && res.status === 409) {
          this.form.get('name')?.setErrors({ alreadyExists: true });
          this.form.get('name')?.markAsTouched();
          this.apiError = 'Please apply the necessary corrections.';
        } else {
          this.apiError = this.isEditMode
            ? 'Failed to update feature. Please try again later.'
            : 'Failed to create feature. Please try again later.';
        }
      },
      error: (err: any) => {
        if (!this.isEditMode && err?.error?.status === 409) {
          this.form.get('name')?.setErrors({ alreadyExists: true });
          this.form.get('name')?.markAsTouched();
          this.apiError = 'Please apply the necessary corrections.';
        } else {
          this.apiError = this.isEditMode
            ? 'Failed to update feature. Please try again later.'
            : 'Failed to create feature. Please try again later.';
        }
        this.spinner.hide().catch(console.error);
      },
      complete: () => {
        this.spinner.hide().catch(console.error);
      },
    });
  }

  close(): void {
    this.dialogRef.close(false);
  }
}
